import { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { useAuth, GsRole, hasRole } from '@gs/auth';
import { ALL_APPS, COMING_APPS } from '../data/apps';
import { useFavorites } from '../hooks/useFavorites';
import { AppGrid } from '../components/AppGrid';
import { FilterBar, type HubFilter } from '../components/FilterBar';

function filterApps(filter: HubFilter) {
  if (filter === 'all') return ALL_APPS;
  if (filter === 'azure') return ALL_APPS.filter((a) => a.platform === 'azure');
  if (filter === 'firebase') return ALL_APPS.filter((a) => a.platform === 'firebase');
  if (filter === 'production') return ALL_APPS.filter((a) => a.status === 'prod');
  if (filter === 'dev') return ALL_APPS.filter((a) => a.status === 'dev');
  if (filter === 'poc') return ALL_APPS.filter((a) => a.status === 'poc');
  return ALL_APPS;
}

export default function AppsPage() {
  const [activeFilter, setActiveFilter] = useState<HubFilter>('all');
  const { toggle, isFav } = useFavorites();
  const { user } = useAuth();
  const isAdmin = hasRole(user, GsRole.ADMIN);

  const filtered = useMemo(() => {
    const base = filterApps(activeFilter);
    return isAdmin ? base : base.filter((a) => !a.adminOnly);
  }, [activeFilter, isAdmin]);

  return (
    <div className="space-y-8 pb-8">
      <div className="flex gap-3 rounded-xl border border-sky-100 bg-sky-50/80 px-4 py-3 text-sm text-sky-900 dark:border-sky-900/40 dark:bg-sky-950/40 dark:text-sky-100">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-sky-600 dark:text-sky-400" />
        <p>
          Cliquez l’étoile sur une application pour l’ajouter à votre tableau de bord personnel (page d’accueil).
        </p>
      </div>

      <FilterBar activeFilter={activeFilter} onChange={setActiveFilter} />

      <AppGrid apps={filtered} showStar isFav={isFav} onToggleFav={toggle} />

      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          À venir
        </h3>
        <div className="flex flex-wrap gap-2">
          {COMING_APPS.map((c) => (
            <span
              key={c.id}
              className="rounded-full border border-dashed border-slate-300 bg-slate-50 px-3 py-1.5 text-xs text-slate-600 dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-300"
              title={c.desc}
            >
              {c.name}
            </span>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-200 pt-6 text-center text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
        GS-Apps · Groupe Signalisation 2026
      </footer>
    </div>
  );
}
